import { useMcpToolsMetadataQuery } from "@qw/graphql";
import { findCalledToolNamesInSteps, MCPPlatform, MCPToolName, PlaybookType } from "@qw/qw-common";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Group,
  Icon,
  Label,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import { type FC, useMemo, useRef } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";

import type { PlaybookFormValues } from "./playbook-editor.types";

const BLOCK_TEXT_TAGS = new Set([
  "address",
  "article",
  "aside",
  "blockquote",
  "div",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "li",
  "main",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "td",
  "th",
  "tr",
  "ul",
]);

function extractWorkflowText(root: ParentNode): string {
  const fragments: string[] = [];

  const visit = (node: ChildNode) => {
    if (node.nodeType === 3) {
      fragments.push(node.textContent ?? "");
      return;
    }
    if (node.nodeType !== 1) {
      return;
    }

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();
    if (tagName === "br") {
      fragments.push("\n");
      return;
    }

    const isBlock = BLOCK_TEXT_TAGS.has(tagName);
    if (isBlock) {
      fragments.push("\n");
    }
    element.childNodes.forEach(visit);
    if (isBlock) {
      fragments.push("\n");
    }
  };

  root.childNodes.forEach(visit);

  return fragments
    .join("")
    .replace(/[ \t\f\v]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * The two ways a workflow can name a tool it uses.
 *
 * `chipped` are the `<span data-type="chat-tool-name" data-tool-name="toolName">` chips the editor
 * inserts, which the save path already auto-adds to the filter. `called` are call signatures in
 * plain prose, which it deliberately does not — a 37,625-character workflow with no chips at all
 * is how `Orbit 5 · Transmit` kept a whitelist forbidding the transitions its own steps called.
 */
function extractToolsFromWorkflow(workflowHtml: string): { chipped: string[]; called: MCPToolName[] } {
  if (!workflowHtml) {
    return { chipped: [], called: [] };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(workflowHtml, "text/html");
  const toolSpans = doc.querySelectorAll('span[data-type="chat-tool-name"]');

  const tools = new Set<string>();
  toolSpans.forEach((span) => {
    const toolName = span.getAttribute("data-tool-name");
    if (toolName) {
      tools.add(toolName);
    }
  });

  return {
    chipped: Array.from(tools).sort((a, b) => a.localeCompare(b)),
    called: findCalledToolNamesInSteps(extractWorkflowText(doc.body)),
  };
}

interface PlaybookToolFilterFieldProps {
  canEdit: boolean;
}

type FilterMode = "all" | "whitelist" | "blacklist";

export const PlaybookToolFilterField: FC<PlaybookToolFilterFieldProps> = ({ canEdit }) => {
  const { control } = useFormContext<PlaybookFormValues>();
  const { field } = useController({ name: "toolFilter", control });

  // Watch playbook type to disable tool filter for "Contextual Background Knowledge" playbooks
  const playbookType = useWatch<PlaybookFormValues, "type">({ name: "type", control });

  // Watch workflow to extract tools in use
  const workflow = useWatch<PlaybookFormValues, "workflow">({ name: "workflow", control });
  const { chipped: workflowTools, called: calledTools } = useMemo(() => extractToolsFromWorkflow(workflow), [workflow]);

  // Tool filter is meaningless for playbooks that never execute actions.
  const isDisabled = !canEdit || playbookType === PlaybookType.Knowledge || playbookType === PlaybookType.Template;

  // Fetch tool metadata for descriptions
  const { data: toolsData } = useMcpToolsMetadataQuery({
    variables: { input: { platforms: [MCPPlatform.INBOX] } },
  });

  // Create a lookup map for tool metadata
  const toolMetadataMap = useMemo(() => {
    const map = new Map<string, { description?: string | null; shortDescription?: string | null }>();
    toolsData?.mcpToolsMetadata?.forEach((tool) => {
      map.set(tool.name, { description: tool.description, shortDescription: tool.shortDescription });
    });
    return map;
  }, [toolsData?.mcpToolsMetadata]);

  // Backup storage for whitelist/blacklist selections (does not survive page refresh)
  const whitelistBackup = useRef<string[]>([]);
  const blacklistBackup = useRef<string[]>([]);

  // Determine current mode from toolFilter value
  // Note: GraphQL returns both whitelist and blacklist fields (one as null), so it must
  // check that the property value is non-null, not just that the property exists.
  const mode: FilterMode = useMemo(() => {
    if (field.value === null || field.value === undefined) {
      return "all";
    }
    if (typeof field.value === "object" && "whitelist" in field.value && field.value.whitelist != null) {
      return "whitelist";
    }
    if (typeof field.value === "object" && "blacklist" in field.value && field.value.blacklist != null) {
      return "blacklist";
    }
    return "all";
  }, [field.value]);

  const selectedTools = useMemo(() => {
    if (mode === "all") {
      return [];
    }
    if (mode === "whitelist" && field.value && "whitelist" in field.value) {
      return field.value.whitelist || [];
    }
    if (mode === "blacklist" && field.value && "blacklist" in field.value) {
      return field.value.blacklist || [];
    }
    return [];
  }, [mode, field.value]);

  const handleModeChange = (newMode: FilterMode) => {
    // Save current selection to backup before switching
    if (mode === "whitelist" && selectedTools.length > 0) {
      whitelistBackup.current = selectedTools;
    } else if (mode === "blacklist" && selectedTools.length > 0) {
      blacklistBackup.current = selectedTools;
    }

    // Switch to new mode, restoring from backup if available
    if (newMode === "all") {
      field.onChange(null);
    } else if (newMode === "whitelist") {
      field.onChange({ whitelist: whitelistBackup.current });
    } else if (newMode === "blacklist") {
      field.onChange({ blacklist: blacklistBackup.current });
    }
  };

  const handleToolToggle = (toolName: string) => {
    if (mode === "all") {
      return;
    }

    const currentTools = new Set(selectedTools);
    if (currentTools.has(toolName)) {
      currentTools.delete(toolName);
    } else {
      currentTools.add(toolName);
    }

    const newTools = Array.from(currentTools);
    if (mode === "whitelist") {
      field.onChange({ whitelist: newTools });
    } else {
      field.onChange({ blacklist: newTools });
    }
  };

  // Get all tool names from the enum, sorted alphabetically
  const allTools = Object.values(MCPToolName).sort((a, b) => a.localeCompare(b));

  // For whitelist suggestions: workflow tools not yet allowed
  const missingWorkflowTools = useMemo(
    () => workflowTools.filter((tool) => !selectedTools.includes(tool)),
    [workflowTools, selectedTools]
  );

  // For blacklist suggestions: tools not used in workflow and not yet prohibited
  const unusedUnprohibitedTools = useMemo(
    () => allTools.filter((tool) => !workflowTools.includes(tool) && !selectedTools.includes(tool)),
    [allTools, workflowTools, selectedTools]
  );

  // For whitelist cleanup: tools allowed but not used in workflow
  const allowedButUnusedTools = useMemo(
    () => selectedTools.filter((tool) => !workflowTools.includes(tool)),
    [selectedTools, workflowTools]
  );

  // For blacklist cleanup: tools prohibited but used in workflow
  const prohibitedButUsedTools = useMemo(
    () => selectedTools.filter((tool) => workflowTools.includes(tool)),
    [selectedTools, workflowTools]
  );

  // Tools the steps call in prose that this filter withholds. Warned about rather than tinted like
  // the lists above, because a withheld tool is hidden from Alby rather than refused: the call
  // never happens and the run still reports success. Non-executing playbook types ignore filters,
  // so a warning about them would be noise. Widened to `string[]` at this boundary because the form
  // holds a tool filter as plain strings, and these compose with `selectedTools`.
  const calledButExcludedTools = useMemo<string[]>(() => {
    if (mode === "all" || playbookType === PlaybookType.Knowledge || playbookType === PlaybookType.Template) {
      return [];
    }
    const excluded =
      mode === "whitelist"
        ? calledTools.filter((tool) => !selectedTools.includes(tool))
        : calledTools.filter((tool) => selectedTools.includes(tool));
    return excluded.sort((a, b) => a.localeCompare(b));
  }, [mode, playbookType, calledTools, selectedTools]);

  // A whitelist withholds a tool by omitting it, a blacklist by listing it, so both the wording of
  // the warning and the repair behind its one click hinge on the mode. Named once here rather than
  // re-branched at each of the three places the block below needs it.
  const withheldCount = `${calledButExcludedTools.length} tool${calledButExcludedTools.length !== 1 ? "s" : ""}`;
  const withheld =
    mode === "blacklist"
      ? {
          verb: "prohibits",
          action: `Stop prohibiting ${withheldCount}`,
          repair: () => ({ blacklist: selectedTools.filter((tool) => !calledButExcludedTools.includes(tool)) }),
        }
      : {
          verb: "is not allowed to use",
          action: `Allow ${withheldCount}`,
          repair: () => ({ whitelist: [...selectedTools, ...calledButExcludedTools] }),
        };

  // Determine color hint for each tool based on active suggestions
  // Cyan = would be added by suggestion, Pink = would be removed by suggestion
  const getToolColorHint = (tool: string): { type: "add" | "remove"; tooltip: string } | null => {
    if (mode === "whitelist") {
      if (missingWorkflowTools.includes(tool)) {
        return { type: "add", tooltip: "Used in workflow but not allowed" };
      }
      if (allowedButUnusedTools.includes(tool)) {
        return { type: "remove", tooltip: "Allowed but not used in workflow" };
      }
    } else if (mode === "blacklist") {
      if ((unusedUnprohibitedTools as string[]).includes(tool)) {
        return { type: "add", tooltip: "Not used in workflow" };
      }
      if (prohibitedButUsedTools.includes(tool)) {
        return { type: "remove", tooltip: "Prohibited but used in workflow" };
      }
    }
    return null;
  };

  return (
    <Group className="space-y-1">
      <div>
        <Label className="text-sm font-medium">Tool Access</Label>
        <p className="text-xs text-muted-foreground mt-1 mb-1">
          Configure which tools this playbook can use during execution.
        </p>
        {playbookType === PlaybookType.Knowledge && (
          <p className="text-xs text-muted-foreground mt-1 mb-2 p-2 bg-muted rounded border border-border">
            <Icon icon="info" className="h-3.5 w-3.5 inline mr-1" />
            Tool filtering is not available for "Contextual Background Knowledge" playbooks since they provide context
            rather than execute actions.
          </p>
        )}
        <Collapsible className="mb-3">
          <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
            <Icon icon="info" className="h-3.5 w-3.5" />
            <span>About tool access</span>
            <Icon icon="keyboard_arrow_down" className="h-4 w-4" />
          </CollapsibleTrigger>
          <CollapsibleContent className="text-xs text-muted-foreground mt-2 pl-5 space-y-1.5">
            <p>
              Tool filters improve focus, quality, and control by restricting which tools Alby can use when executing
              this playbook. By default, all tools are available. You can restrict access by allowing only specific
              tools or by prohibiting specific tools.
            </p>
            <p>
              <strong>How it works:</strong> Filtered tools are completely hidden from Alby—it simply doesn't know they
              exist. This isn't just access control; it shapes what Alby considers possible, leading to more focused and
              predictable behavior.
            </p>
            <p>
              <strong>What counts as a tool?</strong> Tools include all MCP tools (search, send email, create quote,
              etc.) plus special capabilities like <code className="bg-muted px-1 rounded">elicit</code> (pause for user
              input) and <code className="bg-muted px-1 rounded">executePlaybook</code> (run child playbooks).
            </p>
            <p>
              <strong>Always available:</strong> The utility tools{" "}
              <code className="bg-muted px-1 rounded">webSearch</code> and{" "}
              <code className="bg-muted px-1 rounded">alert</code> are always available regardless of your filter
              settings. These lightweight tools support agent operation and are not subject to filtering.
            </p>
            <p>
              <strong>Playbook-level control:</strong> Each playbook uses its own tool filter when executed. If this
              playbook spawns a child playbook, the child uses its own filter—not this one. This allows independent
              policies per playbook.
            </p>
            <p>
              Restricting tools to only what's needed for a task reduces noise, improves reliability, and gives you
              fine-grained control over playbook capabilities.
            </p>
          </CollapsibleContent>
        </Collapsible>

        <RadioGroup value={mode} onValueChange={handleModeChange} disabled={isDisabled} className="space-y-0">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="all" id="tool-mode-all" />
            <Label htmlFor="tool-mode-all" className="text-sm font-normal cursor-pointer">
              All tools allowed
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="whitelist" id="tool-mode-whitelist" />
            <Label htmlFor="tool-mode-whitelist" className="text-sm font-normal cursor-pointer">
              Only allow specific tools
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="blacklist" id="tool-mode-blacklist" />
            <Label htmlFor="tool-mode-blacklist" className="text-sm font-normal cursor-pointer">
              Prohibit specific tools
            </Label>
          </div>
        </RadioGroup>
      </div>

      {calledButExcludedTools.length > 0 && (
        <Alert variant="warning" icon="warning" className="p-3 pt-2.5 text-xs">
          <AlertTitle className="text-xs font-semibold">
            This workflow calls {withheldCount} it {withheld.verb}.
          </AlertTitle>
          <AlertDescription className="text-xs gap-1">
            <p className="font-mono break-words">{calledButExcludedTools.join(", ")}</p>
            <p>
              Withheld tools are hidden from Alby rather than refused, so these calls never happen and the run still
              finishes without an error.
            </p>
            {!isDisabled && (
              <Button
                type="button"
                variant="link"
                className="self-start text-xs"
                onClick={() => field.onChange(withheld.repair())}
              >
                {withheld.action}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {mode !== "all" && (
        <div className="border border-border rounded-lg p-3 bg-muted space-y-2 min-w-0">
          <div className="flex items-center justify-between text-xs font-medium text-foreground">
            <span>{mode === "whitelist" ? "Allowed Tools" : "Prohibited Tools"}</span>
            {!isDisabled && (
              <span className="flex items-center gap-1 font-normal">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() =>
                        field.onChange(mode === "whitelist" ? { whitelist: allTools } : { blacklist: allTools })
                      }
                    >
                      All
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{mode === "whitelist" ? "Allow all tools" : "Prohibit all tools"}</TooltipContent>
                </Tooltip>
                <span className="text-muted-foreground">·</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => field.onChange(mode === "whitelist" ? { whitelist: [] } : { blacklist: [] })}
                    >
                      None
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{mode === "whitelist" ? "Allow no tools" : "Prohibit no tools"}</TooltipContent>
                </Tooltip>
              </span>
            )}
          </div>
          <div className="max-h-[100px] overflow-y-scroll overflow-x-hidden space-y-0 pr-1">
            {allTools.map((tool) => {
              const metadata = toolMetadataMap.get(tool);
              const description = metadata?.shortDescription || metadata?.description;
              const colorHint = getToolColorHint(tool);
              return (
                <Tooltip key={tool}>
                  <TooltipTrigger asChild>
                    <label className="flex items-center gap-1.5 cursor-pointer hover:bg-background px-1.5 py-0.5 rounded text-xs min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedTools.includes(tool)}
                        onChange={() => handleToolToggle(tool)}
                        disabled={isDisabled}
                        className="h-3 w-3 shrink-0 rounded border-input"
                      />
                      <span className="font-mono text-xs truncate">
                        {tool}
                        {colorHint?.type === "add" && <span className="text-cyan-700 ml-0.5">●</span>}
                        {colorHint?.type === "remove" && <span className="text-pink-700 ml-0.5">●</span>}
                      </span>
                    </label>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs">
                    <div className="font-mono text-xs">{tool}</div>
                    {description && <div className="text-xs text-muted-foreground mt-1">{description}</div>}
                    {colorHint && (
                      <div className={`text-xs mt-1 ${colorHint.type === "add" ? "text-cyan-700" : "text-pink-700"}`}>
                        {colorHint.tooltip}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}

      {mode === "whitelist" && (
        <div className="text-[11px] text-muted-foreground space-y-0.5">
          <p>
            {selectedTools.length === 0
              ? "🚫 No tools allowed. This playbook cannot use any tools."
              : `✅ ${selectedTools.length} tool${selectedTools.length !== 1 ? "s" : ""} allowed.`}
          </p>
          {!isDisabled && missingWorkflowTools.length > 0 && (
            <p>
              <span className="inline-block w-2 h-2 rounded-full bg-cyan-700 mr-1" />
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[11px] text-cyan-700 hover:opacity-80"
                onClick={() => field.onChange({ whitelist: [...selectedTools, ...missingWorkflowTools] })}
              >
                Add {missingWorkflowTools.length} tool{missingWorkflowTools.length !== 1 ? "s" : ""} used in workflow.
              </Button>
            </p>
          )}
          {!isDisabled && allowedButUnusedTools.length > 0 && (
            <p>
              <span className="inline-block w-2 h-2 rounded-full bg-pink-700 mr-1" />
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[11px] text-pink-700 hover:opacity-80"
                onClick={() =>
                  field.onChange({ whitelist: selectedTools.filter((t) => !allowedButUnusedTools.includes(t)) })
                }
              >
                Remove {allowedButUnusedTools.length} tool{allowedButUnusedTools.length !== 1 ? "s" : ""} not used in
                workflow.
              </Button>
            </p>
          )}
        </div>
      )}

      {mode === "blacklist" && (
        <div className="text-[11px] text-muted-foreground space-y-0.5">
          <p>
            {selectedTools.length === 0
              ? "✅ All tools available (none prohibited)."
              : `🔒 ${selectedTools.length} tool${selectedTools.length !== 1 ? "s" : ""} prohibited, ${
                  allTools.length - selectedTools.length
                } allowed.`}
          </p>
          {!isDisabled && unusedUnprohibitedTools.length > 0 && (
            <p>
              <span className="inline-block w-2 h-2 rounded-full bg-cyan-700 mr-1" />
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[11px] text-cyan-700 hover:opacity-80"
                onClick={() => field.onChange({ blacklist: [...selectedTools, ...unusedUnprohibitedTools] })}
              >
                Prohibit {unusedUnprohibitedTools.length} unused tool{unusedUnprohibitedTools.length !== 1 ? "s" : ""}.
              </Button>
            </p>
          )}
          {!isDisabled && prohibitedButUsedTools.length > 0 && (
            <p>
              <span className="inline-block w-2 h-2 rounded-full bg-pink-700 mr-1" />
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[11px] text-pink-700 hover:opacity-80"
                onClick={() =>
                  field.onChange({ blacklist: selectedTools.filter((t) => !prohibitedButUsedTools.includes(t)) })
                }
              >
                Stop prohibiting {prohibitedButUsedTools.length} tool
                {prohibitedButUsedTools.length !== 1 ? "s" : ""} used in workflow.
              </Button>
            </p>
          )}
        </div>
      )}

      {mode === "all" && (
        <div className="text-[11px] text-muted-foreground space-y-0.5">
          <p>⚠️ All {allTools.length} tools are available to this playbook.</p>
          {!isDisabled && workflowTools.length > 0 && (
            <p>
              💡{" "}
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[11px]"
                onClick={() => field.onChange({ whitelist: workflowTools })}
              >
                Restrict to {workflowTools.length} tool{workflowTools.length !== 1 ? "s" : ""} used in workflow.
              </Button>
            </p>
          )}
          {!isDisabled && workflowTools.length === 0 && (
            <p>
              No tools used in workflow.{" "}
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[11px]"
                onClick={() => field.onChange({ whitelist: [] })}
              >
                Disallow all tools.
              </Button>
            </p>
          )}
        </div>
      )}
    </Group>
  );
};
